import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { payoutDestinationsApi } from '../api/payoutDestinationsApi';
import { useToast } from '../../../shared/components/toast/useToast';
import type { CreatePayoutDestinationPayload } from '../types';

const errMsg = (err: unknown, fallback: string) => ((err as Error)?.message) || fallback;

export const useMerchantPayoutDestination = (tenantId: string) =>
  useQuery({
    queryKey: ['payout-destination', tenantId],
    queryFn: () => payoutDestinationsApi.get(tenantId),
    staleTime: 60 * 1000,
    enabled: !!tenantId,
  });

export const useBanks = (enabled = true) =>
  useQuery({
    queryKey: ['payout-destination', 'banks'],
    queryFn: payoutDestinationsApi.listBanks,
    staleTime: 24 * 60 * 60 * 1000, // bank list is effectively static
    enabled,
  });

export const useResolveAccount = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ accountNumber, bankCode }: { accountNumber: string; bankCode: string }) =>
      payoutDestinationsApi.resolveAccount(accountNumber, bankCode),
    onError: (err: unknown) => toast.error(errMsg(err, 'Could not resolve account')),
  });
};

export const useCreatePayoutDestination = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreatePayoutDestinationPayload) => payoutDestinationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payout-destination', tenantId] });
      toast.success('Payout destination registered');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to register payout destination')),
  });
};
